/**
 * RespostaAnexosManager - Gerencia anexos na Resposta de Fornecedor (Fase 2)
 *
 * 2 grupos visualmente distintos:
 *  - Catálogo do Fornecedor (vinculado AO FORNECEDOR, reutilizável em cotações futuras)
 *  - Outros Anexos (vinculados APENAS a esta cotação)
 *
 * Props:
 * - cotacaoId: id da cotação
 * - fornecedorId: id do fornecedor selecionado
 * - onCountChange?: callback(catalogos.length, avulsos.length) — para indicador externo
 */
import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import api from '../../lib/axios';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Card } from '../ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import {
  FileText, Upload, Trash2, BookOpen, Paperclip, Loader2, Edit2, Check, X, Eye, Link as LinkIcon, RefreshCw,
} from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

const formatBytes = (b) => {
  if (!b) return '';
  if (b < 1024) return `${b} B`;
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(1)} KB`;
  return `${(b / (1024 * 1024)).toFixed(1)} MB`;
};

const stableUrl = (anexo) => {
  const url = anexo?.url || '';
  if (url.startsWith('/api/')) return url;
  if (url.includes('amazonaws.com') || url.includes('s3.') || url.startsWith('s3://')) {
    const filename = (url.split('?')[0] || '').split('/').pop();
    return `/api/anexos/${filename}`;
  }
  return url;
};

const canPreview = (tipo, nome) => {
  const ext = (nome || '').split('.').pop()?.toLowerCase() || '';
  if ((tipo || '').includes('pdf') || ext === 'pdf') return 'pdf';
  if ((tipo || '').includes('image') || ['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg'].includes(ext)) return 'image';
  return null;
};

const RespostaAnexosManager = ({ cotacaoId, fornecedorId, onCountChange }) => {
  const [loading, setLoading] = useState(false);
  const [uploadingTipo, setUploadingTipo] = useState(null); // "catalogo" | "outro" | null
  const [data, setData] = useState({
    catalogos_resposta: [],
    catalogos_fornecedor: [],
    anexos_avulsos: [],
  });
  const [editing, setEditing] = useState(null); // { id, scope, nome_exibicao, descricao }
  const [showVincular, setShowVincular] = useState(false);
  const [pendingFile, setPendingFile] = useState(null); // {file, tipo}
  const [meta, setMeta] = useState({ nome_exibicao: '', descricao: '' });
  const [previewAnexo, setPreviewAnexo] = useState(null); // anexo aberto no preview inline

  const fetchAll = useCallback(async () => {
    if (!cotacaoId || !fornecedorId) return;
    setLoading(true);
    try {
      const r = await api.get(`/cotacoes/${cotacaoId}/resposta/${fornecedorId}/anexos`);
      const next = {
        catalogos_resposta: r.data?.catalogos_resposta || [],
        catalogos_fornecedor: r.data?.catalogos_fornecedor || [],
        anexos_avulsos: r.data?.anexos_avulsos || [],
      };
      setData(next);
      if (onCountChange) onCountChange(next.catalogos_resposta.length, next.anexos_avulsos.length);
    } catch (err) {
      console.error('Erro ao carregar anexos da resposta:', err);
    } finally {
      setLoading(false);
    }
  }, [cotacaoId, fornecedorId, onCountChange]);

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  const triggerFileInput = (tipo) => {
    const inp = document.createElement('input');
    inp.type = 'file';
    inp.multiple = true;
    inp.accept = '.pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.csv,.txt,.zip,.rar,.7z,.dwg,.dxf,.stp,.step,.igs,.iges,.stl,.3dm,.sldprt,.sldasm,.ipt,.iam,.prt,.x_t,.x_b,.sat,image/*';
    inp.onchange = (e) => {
      const files = Array.from(e.target.files || []);
      if (files.length === 0) return;
      if (files.length === 1) {
        // Fluxo single: abre dialog de metadados (nome de exibição + descrição)
        const f = files[0];
        setPendingFile({ file: f, tipo });
        setMeta({ nome_exibicao: f.name.replace(/\.[^/.]+$/, ''), descricao: '' });
      } else {
        // Fluxo múltiplo: envia todos em sequência com nome padrão (= nome do arquivo)
        uploadMany(files, tipo);
      }
    };
    inp.click();
  };

  const uploadOne = async (file, tipo, metaObj = {}) => {
    const fd = new FormData();
    fd.append('file', file);
    fd.append('tipo', tipo);
    if (metaObj.nome_exibicao) fd.append('nome_exibicao', metaObj.nome_exibicao);
    if (metaObj.descricao) fd.append('descricao', metaObj.descricao);
    const token = localStorage.getItem('nautilus_token');
    await axios.post(
      `${API}/api/cotacoes/${cotacaoId}/resposta/${fornecedorId}/anexos`,
      fd,
      { headers: { Authorization: `Bearer ${token}` } }
    );
  };

  const uploadMany = async (files, tipo) => {
    const grandes = files.filter(f => f.size > 50 * 1024 * 1024);
    if (grandes.length > 0) {
      toast.error(`${grandes.length} arquivo(s) acima de 50MB serão ignorados`);
    }
    const validos = files.filter(f => f.size <= 50 * 1024 * 1024);
    if (validos.length === 0) return;

    setUploadingTipo(tipo);
    let ok = 0;
    let falhas = 0;
    for (const file of validos) {
      try {
        // Nome completo (com extensão) evita colisão entre arquivos de mesmo nome-base
        await uploadOne(file, tipo, { nome_exibicao: file.name });
        ok += 1;
      } catch (err) {
        falhas += 1;
      }
    }
    if (ok > 0) {
      toast.success(`${ok} arquivo(s) enviado(s)${tipo === 'catalogo' ? ' e vinculado(s) ao fornecedor' : ''}`);
    }
    if (falhas > 0) {
      toast.error(`${falhas} arquivo(s) falharam no envio`);
    }
    setUploadingTipo(null);
    await fetchAll();
  };

  const handleConfirmUpload = async () => {
    if (!pendingFile) return;
    const { file, tipo } = pendingFile;
    if (file.size > 50 * 1024 * 1024) {
      toast.error('Arquivo muito grande (máx 50MB)');
      return;
    }
    setUploadingTipo(tipo);
    try {
      await uploadOne(file, tipo, meta);
      toast.success(tipo === 'catalogo'
        ? 'Catálogo enviado e vinculado ao fornecedor'
        : 'Anexo enviado'
      );
      setPendingFile(null);
      setMeta({ nome_exibicao: '', descricao: '' });
      await fetchAll();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Erro ao enviar arquivo');
    } finally {
      setUploadingTipo(null);
    }
  };

  const handleRemove = async (anexo, tipo, scope) => {
    const ok = window.confirm(
      tipo === 'catalogo' && scope === 'master'
        ? `Remover este catálogo do cadastro do fornecedor? Ele não estará mais disponível em outras cotações.`
        : `Remover este anexo desta cotação?`
    );
    if (!ok) return;

    try {
      if (scope === 'master') {
        // Catálogo no cadastro do fornecedor — deleta do fornecedor + da cotação
        await api.delete(
          `/cotacoes/${cotacaoId}/resposta/${fornecedorId}/anexos/${anexo.id}?tipo=catalogo&remover_do_fornecedor=true`
        );
      } else {
        await api.delete(
          `/cotacoes/${cotacaoId}/resposta/${fornecedorId}/anexos/${anexo.id}?tipo=${tipo}`
        );
      }
      toast.success('Removido');
      fetchAll();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Erro ao remover');
    }
  };

  const [reconciling, setReconciling] = useState(false);
  const handleReconciliar = async () => {
    setReconciling(true);
    try {
      const r = await api.post(`/cotacoes/${cotacaoId}/resposta/${fornecedorId}/reconciliar-anexos`);
      const rec = (r.data?.recuperados_avulsos || 0) + (r.data?.recuperados_catalogos || 0);
      if (rec > 0) {
        toast.success(`${rec} anexo(s) recuperado(s)`);
        await fetchAll();
      } else {
        toast.info('Nenhum anexo pendente para recuperar');
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Erro ao recuperar anexos');
    } finally {
      setReconciling(false);
    }
  };

  const handleVincular = async (catalogo) => {
    try {
      await api.post(
        `/cotacoes/${cotacaoId}/resposta/${fornecedorId}/vincular-catalogo`,
        { catalogo_id: catalogo.id }
      );
      toast.success('Catálogo vinculado a esta resposta');
      setShowVincular(false);
      fetchAll();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Erro ao vincular');
    }
  };

  // Catálogos do fornecedor que ainda NÃO estão nesta resposta
  const idsNaResposta = new Set((data.catalogos_resposta || []).map(c => c.id));
  const catalogosDisponiveis = (data.catalogos_fornecedor || []).filter(c => !idsNaResposta.has(c.id));

  return (
    <div className="space-y-4">
      <div className="flex justify-end">
        <Button
          type="button"
          size="sm"
          variant="ghost"
          className="text-xs text-gray-500 hover:text-gray-800"
          onClick={handleReconciliar}
          disabled={reconciling}
          title="Recupera anexos que foram enviados mas sumiram da lista (re-vincula os arquivos já salvos no armazenamento)"
          data-testid="btn-reconciliar-anexos"
        >
          {reconciling ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <RefreshCw className="w-3 h-3 mr-1" />}
          Recuperar anexos
        </Button>
      </div>
      {/* === BLOCO 1: Catálogo do Fornecedor === */}
      <Card className="p-4 border-blue-200 bg-blue-50/40" data-testid="resposta-anexos-catalogo">
        <div className="flex items-start justify-between gap-2 mb-2">
          <div className="flex items-center gap-2">
            <BookOpen className="w-4 h-4 text-blue-600" />
            <Label className="text-sm font-semibold text-blue-900">Catálogo do Fornecedor</Label>
          </div>
          <div className="flex items-center gap-2">
            {catalogosDisponiveis.length > 0 && (
              <Button
                type="button"
                size="sm"
                variant="outline"
                onClick={() => setShowVincular(true)}
                data-testid="btn-vincular-catalogo"
              >
                <LinkIcon className="w-3 h-3 mr-1" />
                Vincular existente ({catalogosDisponiveis.length})
              </Button>
            )}
            <Button
              type="button"
              size="sm"
              variant="default"
              className="bg-blue-600 hover:bg-blue-700"
              onClick={() => triggerFileInput('catalogo')}
              disabled={uploadingTipo === 'catalogo'}
              data-testid="btn-upload-catalogo"
            >
              {uploadingTipo === 'catalogo' ? (
                <Loader2 className="w-3 h-3 mr-1 animate-spin" />
              ) : (
                <Upload className="w-3 h-3 mr-1" />
              )}
              Enviar catálogo
            </Button>
          </div>
        </div>
        <p className="text-xs text-blue-700 mb-3">
          Será salvo no <strong>cadastro do fornecedor</strong> e ficará disponível em cotações futuras.
        </p>

        {data.catalogos_resposta.length === 0 ? (
          <p className="text-xs text-gray-500 italic">Nenhum catálogo vinculado a esta resposta ainda.</p>
        ) : (
          <ul className="space-y-2">
            {data.catalogos_resposta.map((a) => (
              <AnexoRow
                key={a.id}
                anexo={a}
                tipo="catalogo"
                onRemove={() => handleRemove(a, 'catalogo', 'resposta')}
                onRemoveMaster={() => handleRemove(a, 'catalogo', 'master')}
                onPreview={setPreviewAnexo}
                showMasterDelete
              />
            ))}
          </ul>
        )}
      </Card>

      {/* === BLOCO 2: Outros Anexos === */}
      <Card className="p-4 border-amber-200 bg-amber-50/40" data-testid="resposta-anexos-outros">
        <div className="flex items-start justify-between gap-2 mb-2">
          <div className="flex items-center gap-2">
            <Paperclip className="w-4 h-4 text-amber-700" />
            <Label className="text-sm font-semibold text-amber-900">Outros Anexos desta Cotação</Label>
          </div>
          <Button
            type="button"
            size="sm"
            variant="outline"
            onClick={() => triggerFileInput('outro')}
            disabled={uploadingTipo === 'outro'}
            data-testid="btn-upload-outro"
          >
            {uploadingTipo === 'outro' ? (
              <Loader2 className="w-3 h-3 mr-1 animate-spin" />
            ) : (
              <Upload className="w-3 h-3 mr-1" />
            )}
            Enviar anexo
          </Button>
        </div>
        <p className="text-xs text-amber-800 mb-3">
          Vinculado <strong>apenas a esta cotação</strong>.
        </p>

        {data.anexos_avulsos.length === 0 ? (
          <p className="text-xs text-gray-500 italic">Nenhum anexo nesta resposta.</p>
        ) : (
          <ul className="space-y-2">
            {data.anexos_avulsos.map((a) => (
              <AnexoRow
                key={a.id}
                anexo={a}
                tipo="outro"
                onRemove={() => handleRemove(a, 'outro', 'resposta')}
                onPreview={setPreviewAnexo}
              />
            ))}
          </ul>
        )}
      </Card>

      {/* Loading indicador */}
      {loading && (
        <div className="text-xs text-gray-500 flex items-center gap-2">
          <Loader2 className="w-3 h-3 animate-spin" />
          Carregando anexos...
        </div>
      )}

      {/* Dialog: metadados antes de upload (nome customizado + descrição) */}
      <Dialog open={!!pendingFile} onOpenChange={(o) => { if (!o) setPendingFile(null); }}>
        <DialogContent className="max-w-md" data-testid="dialog-meta-upload">
          <DialogHeader>
            <DialogTitle>
              {pendingFile?.tipo === 'catalogo' ? 'Enviar Catálogo do Fornecedor' : 'Enviar Anexo'}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div className="text-xs text-gray-600 bg-gray-50 rounded px-3 py-2 flex items-center gap-2 min-w-0">
              <FileText className="w-4 h-4 shrink-0 text-gray-400" />
              <span className="truncate flex-1 min-w-0" title={pendingFile?.file?.name}>
                {pendingFile?.file?.name}
              </span>
              <span className="shrink-0 text-gray-400">
                {pendingFile?.file && formatBytes(pendingFile.file.size)}
              </span>
            </div>
            <div>
              <Label className="text-xs">Nome de exibição</Label>
              <Input
                value={meta.nome_exibicao}
                onChange={(e) => setMeta({ ...meta, nome_exibicao: e.target.value })}
                placeholder="Ex: Catálogo Mercury 2026"
                data-testid="input-nome-exibicao"
              />
            </div>
            <div>
              <Label className="text-xs">Descrição (opcional)</Label>
              <Textarea
                value={meta.descricao}
                onChange={(e) => setMeta({ ...meta, descricao: e.target.value })}
                placeholder="Ex: Tabela de preços oficial recebida por e-mail em 03/03/2026"
                rows={2}
                data-testid="input-descricao"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPendingFile(null)} disabled={!!uploadingTipo}>
              Cancelar
            </Button>
            <Button onClick={handleConfirmUpload} disabled={!!uploadingTipo} data-testid="btn-confirm-upload">
              {uploadingTipo ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Upload className="w-3 h-3 mr-1" />}
              Enviar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog: vincular catálogo existente */}
      <Dialog open={showVincular} onOpenChange={setShowVincular}>
        <DialogContent className="max-w-lg" data-testid="dialog-vincular-catalogo">
          <DialogHeader>
            <DialogTitle>Vincular Catálogo Existente</DialogTitle>
          </DialogHeader>
          <p className="text-xs text-gray-600">
            Catálogos já cadastrados deste fornecedor que ainda não estão nesta resposta:
          </p>
          {catalogosDisponiveis.length === 0 ? (
            <p className="text-sm text-gray-500 py-4 text-center">Nenhum catálogo disponível para vincular.</p>
          ) : (
            <ul className="space-y-2 max-h-72 overflow-y-auto">
              {catalogosDisponiveis.map((c) => (
                <li key={c.id} className="border rounded p-2 hover:bg-blue-50 transition-colors flex items-center gap-2">
                  <FileText className="w-4 h-4 text-blue-500 shrink-0" />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium truncate">{c.nome_exibicao || c.nome}</p>
                    {c.descricao && <p className="text-xs text-gray-500 truncate">{c.descricao}</p>}
                  </div>
                  <Button size="sm" onClick={() => handleVincular(c)} data-testid={`btn-vincular-${c.id}`}>
                    <Check className="w-3 h-3 mr-1" /> Vincular
                  </Button>
                </li>
              ))}
            </ul>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowVincular(false)}>Fechar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog: preview inline (iframe para PDF, img para imagem) */}
      <Dialog open={!!previewAnexo} onOpenChange={(o) => { if (!o) setPreviewAnexo(null); }}>
        <DialogContent className="max-w-5xl w-[95vw] h-[90vh] p-0 flex flex-col" data-testid="dialog-preview-anexo">
          <DialogHeader className="px-4 py-3 border-b flex-row items-center justify-between space-y-0">
            <DialogTitle className="text-sm font-medium truncate pr-4">
              {previewAnexo?.nome_exibicao || previewAnexo?.nome}
            </DialogTitle>
            <Button
              type="button"
              size="sm"
              variant="outline"
              onClick={() => previewAnexo && window.open(stableUrl(previewAnexo), '_blank')}
              data-testid="btn-abrir-nova-aba"
            >
              Nova aba
            </Button>
          </DialogHeader>
          <div className="flex-1 bg-gray-100 overflow-hidden">
            {previewAnexo && canPreview(previewAnexo.tipo_mime || previewAnexo.tipo, previewAnexo.nome) === 'pdf' && (
              <iframe
                src={stableUrl(previewAnexo)}
                className="w-full h-full border-0"
                title={previewAnexo.nome}
                data-testid="iframe-preview-anexo"
              />
            )}
            {previewAnexo && canPreview(previewAnexo.tipo_mime || previewAnexo.tipo, previewAnexo.nome) === 'image' && (
              <div className="w-full h-full flex items-center justify-center p-4 overflow-auto">
                <img
                  src={stableUrl(previewAnexo)}
                  alt={previewAnexo.nome}
                  className="max-w-full max-h-full object-contain"
                  data-testid="img-preview-anexo"
                />
              </div>
            )}
            {previewAnexo && !canPreview(previewAnexo.tipo_mime || previewAnexo.tipo, previewAnexo.nome) && (
              <div className="w-full h-full flex flex-col items-center justify-center gap-3 p-8 text-center">
                <FileText className="w-16 h-16 text-gray-400" />
                <p className="text-sm text-gray-600">
                  Este tipo de arquivo não pode ser visualizado inline.
                </p>
                <Button onClick={() => window.open(stableUrl(previewAnexo), '_blank')}>
                  Abrir em nova aba
                </Button>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

const AnexoRow = ({ anexo, tipo, onRemove, onRemoveMaster, onPreview, showMasterDelete }) => {
  const previewType = canPreview(anexo.tipo_mime || anexo.tipo, anexo.nome);
  const handleClick = () => {
    if (previewType && onPreview) {
      onPreview(anexo);
    } else {
      const url = stableUrl(anexo);
      if (url) window.open(url, '_blank');
    }
  };

  return (
    <li
      className="border rounded p-2 bg-white flex items-start gap-3"
      data-testid={`anexo-row-${tipo}-${anexo.id}`}
    >
      <FileText className={`w-5 h-5 mt-0.5 shrink-0 ${tipo === 'catalogo' ? 'text-blue-500' : 'text-amber-600'}`} />
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium truncate" title={anexo.nome}>
          {anexo.nome_exibicao || anexo.nome}
        </p>
        {anexo.descricao && (
          <p className="text-xs text-gray-600 mt-0.5 line-clamp-2">{anexo.descricao}</p>
        )}
        <p className="text-[10px] text-gray-400 mt-0.5">
          {formatBytes(anexo.tamanho)}
          {anexo.uploaded_at && (() => {
            try {
              return ` · ${new Date(anexo.uploaded_at).toLocaleDateString('pt-BR')}`;
            } catch { return ''; }
          })()}
          {anexo.uploaded_by && ` · ${anexo.uploaded_by}`}
        </p>
      </div>
      <div className="flex items-center gap-1 shrink-0">
        <Button type="button" size="sm" variant="ghost" className="h-7 w-7 p-0" onClick={handleClick} title={previewType ? 'Visualizar' : 'Abrir em nova aba'}>
          <Eye className="w-4 h-4 text-blue-500" />
        </Button>
        {showMasterDelete && (
          <Button
            type="button" size="sm" variant="ghost" className="h-7 w-7 p-0"
            onClick={onRemoveMaster}
            title="Remover do cadastro do fornecedor"
            data-testid={`btn-remove-master-${anexo.id}`}
          >
            <Trash2 className="w-4 h-4 text-red-500" />
          </Button>
        )}
        {!showMasterDelete && (
          <Button
            type="button" size="sm" variant="ghost" className="h-7 w-7 p-0"
            onClick={onRemove}
            title="Remover anexo"
            data-testid={`btn-remove-${anexo.id}`}
          >
            <Trash2 className="w-4 h-4 text-red-500" />
          </Button>
        )}
      </div>
    </li>
  );
};

export default RespostaAnexosManager;
